// <feature-carousel> — behaviour layer for the `siclips-feature-carousel` section.
// One slide is visible at a time: a large image on one side, a synced text block (number+category,
// title, description, optional button) on the other, prev/next arrows flanking the text, and dot
// indicators under the image. Arrows wrap around (next at last -> first, prev at first -> last) and
// each move swaps image + text + dots together.
//
// Build: in this Horizon theme, theme blocks render via {% content_for 'blocks' %} in ONE place, so
// each slide block emits a HIDDEN `.fcr__source` holding a `.fcr__img` pane and a `.fcr__textblock`
// pane. On connect we CLONE the `.fcr__img` into the media column and the `.fcr__textblock` into the
// text panel (both grid-stacked so the container is as tall as the tallest pane -> no CLS), then
// crossfade between them. With zero slides the whole element hides itself.
//
// Transitions are CSS-driven (a `.fcr--fx-*` class restyles the shared `.fcr__pane`): crossfade /
// fade-zoom / fade-rise / instant only toggle `.is-active`. `slide` is direction-aware, so JS
// pre-positions the incoming pane on the entry side and pushes the outgoing pane to the exit side
// (mirrored by dirSign for RTL). Autoplay (optional) uses a single rAF ticker with a Set of pause
// reasons; it never starts under prefers-reduced-motion. Everything animates transform/opacity only.

const SLIDE_SHIFT = 42; // px of horizontal travel for the "slide" transition

class FeatureCarousel extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.cfg = this._readJSON('[data-fcr-config]') || {};
    this.refs = {
      frame: this.querySelector('[data-fcr-frame]'),
      slides: this.querySelector('[data-fcr-slides]'),
      dots: this.querySelector('[data-fcr-dots]'),
      status: this.querySelector('[data-fcr-status]'),
    };
    this.prevBtns = Array.from(this.querySelectorAll('[data-fcr-prev]'));
    this.nextBtns = Array.from(this.querySelectorAll('[data-fcr-next]'));

    // clone the block-rendered sources into the two live columns; false = no slides
    if (!this._build()) return;

    this.rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.rtl = (this.getAttribute('dir') || document.dir || '').toLowerCase() === 'rtl' || !!this.cfg.rtl;
    this.dirSign = this.rtl ? -1 : 1;
    this.fx = this.cfg.fx || 'crossfade';
    this.speed = Math.max(0, +this.cfg.speed || 0);
    this._pauseReasons = new Set();
    this.index = Math.min(Math.max(parseInt(this.cfg.startIndex, 10) || 0, 0), this.n - 1);

    this._buildDots();
    this._bind();
    this._armInitialActivate();
    this._syncDots();
    this._announce();
    this._startAutoplay();
    this.setAttribute('data-fcr-ready', '');
  }

  disconnectedCallback() {
    // allow a theme-editor re-render (block add/reorder) to run connectedCallback again
    this._init = false;
    this._armGen = (this._armGen || 0) + 1; // invalidate any pending decode/timeout arm
    this._teardownAutoplay();
    if (this._ro) { this._ro.disconnect(); this._ro = null; this._roTarget = null; }
    if (this._leaveT) clearTimeout(this._leaveT);
    if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
  }

  // ---------- setup ----------
  _readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Clone each hidden `.fcr__source`'s two panes into the media column and text panel. Cloning (not
  // moving) keeps the source in the DOM as the theme-editor anchor and keeps the copy crawlable.
  _build() {
    const { frame, slides } = this.refs;
    if (!frame || !slides) return false;
    frame.replaceChildren();
    slides.replaceChildren();

    const sources = Array.from(this.querySelectorAll('.fcr__source'));
    this.imgs = [];
    this.texts = [];
    sources.forEach((src) => {
      const img = src.querySelector('.fcr__img');
      const txt = src.querySelector('.fcr__textblock');
      if (!img || !txt) return;
      const ic = img.cloneNode(true);
      const tc = txt.cloneNode(true);
      ic.removeAttribute('data-fcr-img');
      tc.removeAttribute('data-fcr-text');
      frame.appendChild(ic);
      slides.appendChild(tc);
      this.imgs.push(ic);
      this.texts.push(tc);
    });

    this.n = this.imgs.length;
    if (this.n === 0) { this.hidden = true; return false; }
    this.hidden = false;
    return true;
  }

  _buildDots() {
    const wrap = this.refs.dots;
    this.dots = [];
    if (!wrap) return;
    wrap.replaceChildren();
    const clickable = this.cfg.dotsClickable !== false;
    if (!clickable) wrap.classList.add('fcr__dots--static');
    for (let i = 0; i < this.n; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'fcr__dot';
      b.setAttribute('aria-label', `${this.cfg.slideWord || 'שקופית'} ${i + 1}`);
      if (clickable) {
        b.addEventListener('click', () => { this._userInteracted(); this._goToNearest(i); });
      } else {
        b.tabIndex = -1;
      }
      wrap.appendChild(b);
      this.dots.push(b);
    }
  }

  _bind() {
    // ELEMENT listeners bind ONCE PER ELEMENT and are never reset — an editor drag-move
    // disconnects+reconnects the SAME element, and re-binding here made every arrow click
    // advance N+1 slides after N moves (§editor-lifecycle convention, 2026-08-01 audit)
    if (!this._boundEl) {
      this._boundEl = true;
      this.prevBtns.forEach((b) => b.addEventListener('click', () => { this._userInteracted(); this._prev(); }));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => { this._userInteracted(); this._next(); }));

      this.addEventListener('keydown', (e) => this._key(e));

      if (this.cfg.pauseOnHover !== false) {
        this.addEventListener('pointerenter', () => this._pause('hover'));
        this.addEventListener('pointerleave', () => this._resume('hover'));
      }
      this.addEventListener('focusin', () => this._pause('focus'));
      this.addEventListener('focusout', () => this._resume('focus'));

      this._bindSwipe();
    }

    // DOCUMENT listeners re-bind per connect (removed in disconnectedCallback)
    this._onVis = this._onVis || (() => (document.hidden ? this._pause('tab') : this._resume('tab')));
    document.addEventListener('visibilitychange', this._onVis);

    // theme editor: focus/preview the block being edited
    this._onBlockSelect = this._onBlockSelect || ((e) => {
      const sources = Array.from(this.querySelectorAll('.fcr__source'));
      const idx = sources.findIndex((sc) => sc === e.target || sc.contains(e.target));
      if (idx >= 0) this._goToNearest(idx);
    });
    document.addEventListener('shopify:block:select', this._onBlockSelect);
  }

  _bindSwipe() {
    const el = this.refs.frame;
    if (!el) return;
    el.style.touchAction = 'pan-y';
    let x0 = null, y0 = null, id = null;
    el.addEventListener('pointerdown', (e) => {
      if (e.button != null && e.button !== 0) return;
      x0 = e.clientX; y0 = e.clientY; id = e.pointerId;
    });
    const end = (e) => {
      if (x0 == null || e.pointerId !== id) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        this._userInteracted();
        // reading-forward (next): swipe left in LTR, swipe right in RTL
        const goNext = this.rtl ? dx > 0 : dx < 0;
        goNext ? this._next() : this._prev();
      }
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', () => { x0 = null; });
  }

  _key(e) {
    let handled = true;
    if (e.key === 'ArrowRight') this.rtl ? this._prev() : this._next();
    else if (e.key === 'ArrowLeft') this.rtl ? this._next() : this._prev();
    else if (e.key === 'Home') this._step(0, -1);
    else if (e.key === 'End') this._step(this.n - 1, 1);
    else handled = false;
    if (handled) { e.preventDefault(); this._userInteracted(); }
  }

  // ---------- navigation ----------
  _next() { this._step((this.index + 1) % this.n, 1); }
  _prev() { this._step((this.index - 1 + this.n) % this.n, -1); }

  // jump to an absolute index (dots / editor): direction is the shorter visual way there
  _goToNearest(to) {
    to = ((to % this.n) + this.n) % this.n;
    if (to === this.index) return;
    const dir = to > this.index ? 1 : -1;
    this._step(to, dir);
  }

  _step(to, dir) {
    const from = this.index;
    if (to === from) return;
    this.index = to;
    this._animate(from, to, dir);
    this._syncDots();
    this._announce();
  }

  // ---------- rendering ----------
  // Decode-gate the initial activation: wait for the first pane's image to be decoded (bumped to
  // eager/high priority) so the pane's `.is-active` opacity transition fades in over the finished
  // image instead of flashing the browser fallback — capped at 900ms so a slow network can never
  // hold the carousel back. `arm` is idempotent, and a generation token + isConnected guard make
  // sure a pending decode/timeout from a removed element (theme-editor re-render) cannot activate
  // a stale instance.
  _armInitialActivate() {
    const gen = (this._armGen = (this._armGen || 0) + 1);
    let armed = false;
    let timer = 0;
    const arm = () => {
      if (armed) return;
      armed = true;
      if (timer) clearTimeout(timer);
      if (gen !== this._armGen || !this._init || !this.isConnected) return;
      this._activate(this.index);
    };
    const pane = this.imgs[this.index];
    const img = pane && pane.querySelector('img');
    if (!img || img.complete || typeof img.decode !== 'function') { arm(); return; }
    img.loading = 'eager';
    if ('fetchPriority' in img) img.fetchPriority = 'high';
    else img.setAttribute('fetchpriority', 'high');
    timer = setTimeout(arm, 900);
    img.decode().then(arm, arm);
  }

  _activate(cur) {
    this.imgs.forEach((el, i) => this._setActive(el, i === cur));
    this.texts.forEach((el, i) => this._setActive(el, i === cur));
    this._syncPanelHeight();
  }

  // Adaptive panel height ("adapt" mode): the CSS takes inactive panes out of the flow and sizes
  // `.fcr__slides` to var(--fcr-live-h, auto); we measure the ACTIVE pane and keep the var current so the
  // height animates between slides. A ResizeObserver on the active pane re-measures on re-wrap
  // (viewport resize, font swap). Always maintained — harmless in "equal" mode (var unused there).
  _syncPanelHeight() {
    const slides = this.refs.slides;
    const active = this.texts && this.texts[this.index];
    if (!slides || !active) return;
    if (!this._ro && 'ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => this._measurePanel());
    }
    if (this._ro && this._roTarget !== active) {
      if (this._roTarget) this._ro.unobserve(this._roTarget);
      this._ro.observe(active);
      this._roTarget = active;
    }
    this._measurePanel();
  }

  _measurePanel() {
    const slides = this.refs.slides;
    const active = this.texts && this.texts[this.index];
    if (!slides || !active || !active.isConnected) return;
    const h = active.scrollHeight; // content height even while the container is mid-transition
    if (h > 0) slides.style.setProperty('--fcr-live-h', h + 'px');
  }

  _setActive(el, on) {
    el.classList.toggle('is-active', on);
    el.setAttribute('aria-hidden', on ? 'false' : 'true');
    if (on) {
      el.removeAttribute('inert');
      const img = el.querySelector('img');
      if (img) { img.loading = 'eager'; }
      el.querySelectorAll('a, button').forEach((x) => x.removeAttribute('tabindex'));
    } else {
      el.setAttribute('inert', '');
      el.querySelectorAll('a, button').forEach((x) => x.setAttribute('tabindex', '-1'));
    }
  }

  // toggle active panes; for the direction-aware "slide" effect, pre-position incoming/outgoing
  _animate(from, to, dir) {
    const doSlide = this.fx === 'slide' && dir !== 0 && !this.rmq.matches && this.speed > 0;
    if (!doSlide) { this._activate(to); return; }

    const shift = SLIDE_SHIFT * dir * this.dirSign; // entry side; exit side is the negative
    const all = this.imgs.concat(this.texts);
    const inc = [this.imgs[to], this.texts[to]];
    const out = [this.imgs[from], this.texts[from]];

    // freeze transitions and clear any residue, then place the incoming pane on the entry side
    all.forEach((el) => { el.style.transition = 'none'; el.style.transform = ''; el.style.opacity = ''; });
    inc.forEach((el) => { el.style.transform = `translateX(${shift}px)`; el.style.opacity = '0'; });
    void this.refs.frame.offsetWidth; // lock the start state

    // re-enable transitions; incoming animates from the entry side to rest (via .is-active)
    all.forEach((el) => { el.style.transition = ''; });
    inc.forEach((el) => { el.style.transform = ''; el.style.opacity = ''; });
    this._activate(to);
    // outgoing slides out to the exit side while fading
    out.forEach((el) => { el.style.transform = `translateX(${-shift}px)`; el.style.opacity = '0'; });

    clearTimeout(this._leaveT);
    this._leaveT = setTimeout(() => {
      out.forEach((el) => { el.style.transform = ''; el.style.opacity = ''; });
    }, this.speed + 80);
  }

  _syncDots() {
    if (!this.dots) return;
    this.dots.forEach((b, i) => {
      const on = i === this.index;
      b.classList.toggle('is-active', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
  }

  _announce() {
    if (this.refs.status) {
      this.refs.status.textContent = `${this.index + 1} ${this.cfg.ofWord || 'מתוך'} ${this.n}`;
    }
  }

  // ---------- autoplay ----------
  _startAutoplay() {
    if (!this.cfg.autoplay || this.n < 2 || this.rmq.matches) return;
    this.autoplayOn = true;
    this.interval = Math.max(1500, +this.cfg.interval || 5000);
    this._apLast = null;
    this._apElapsed = 0;
    const tick = (now) => {
      this.apRaf = requestAnimationFrame(tick);
      if (this._apLast == null) this._apLast = now;
      if (this._pauseReasons.size > 0) { this._apLast = now; return; }
      this._apElapsed += now - this._apLast;
      this._apLast = now;
      if (this._apElapsed >= this.interval) {
        this._apElapsed = 0;
        this._next();
      }
    };
    this.apRaf = requestAnimationFrame(tick);
  }

  _teardownAutoplay() {
    if (this.apRaf) { cancelAnimationFrame(this.apRaf); this.apRaf = 0; }
  }

  _pause(reason) {
    if (!this.autoplayOn) return;
    this._pauseReasons.add(reason);
  }

  _resume(reason) {
    this._pauseReasons.delete(reason);
    this._apLast = null;
  }

  _userInteracted() {
    this._apElapsed = 0;
    this._apLast = null;
  }
}

if (!customElements.get('feature-carousel')) {
  customElements.define('feature-carousel', FeatureCarousel);
}
